import { useMutation } from '@tanstack/react-query';

import { getApiErrorMessage } from '@/lib/api/client';
import { apiV2Public, postFormData } from '@/lib/api/client-v2';
import { notify } from '@/lib/notify';
import { PUBLIC_ENDPOINTS } from '@/types/api/endpoints';

/** Champs du formulaire de contact ; les pièces sont dans `files` (clé `X` pour l'envoi multiple). */
export interface ContactForm {
    message: string;
    files?: Record<string, File | null | undefined> | (File | null | undefined)[];
    reason?: string;
    mois_echeance?: string | number;
    annee_echeance?: string | number;
}

/** Corps multipart du message, construit comme l'ancien middleware `contact`. */
const toFormData = (form: ContactForm): FormData => {
    const data = new FormData();
    const body: ContactForm & Record<string, unknown> = { ...form, message: form.message.replace(/\n/g, ' ') };

    Object.keys(body).forEach((key) => {
        if (key === 'files') {
            const files = (body.files ?? {}) as Record<string, File | null | undefined>;
            Object.keys(files).forEach((doc) => {
                const file = files[doc];
                if (file && file.name !== undefined && doc !== 'X') {
                    data.append(`file`, file);
                } else if (file && file.name !== undefined) {
                    data.append(`files[${doc}]`, file);
                    data.append(`file`, file);
                }
            });
        } else {
            data.append(key, body[key] as string | Blob);
        }
    });

    if (body['reason']) {
        data.append('motif_contact', body['reason']);
    }

    if (body['mois_echeance'] && body['annee_echeance']) {
        data.append('date_echeance', `${('0' + body['mois_echeance']).slice(-2)}/${body['annee_echeance']}`);
    }

    return data;
};

export interface PostContactVariables {
    form: ContactForm;
    /** Appelée à la confirmation de la popin de succès. */
    successFunction?: () => void;
}

/** Envoi du formulaire de contact (`POST /api/v2/nous-contacter`, public). */
export const usePostContact = () =>
    useMutation({
        mutationFn: ({ form }: PostContactVariables) =>
            postFormData<null>(PUBLIC_ENDPOINTS.contact, toFormData(form), apiV2Public),
        onSuccess: (_data, { successFunction }) => {
            void notify({
                html: '<div><p>Votre message a bien été transmis à notre équipe de gestion.</p></div>',
                type: 'success',
                title: 'Félicitations !',
                confirmButtonText: "J'ai compris",
                successFunction,
            } as Parameters<typeof notify>[0]);
        },
        onError: (error) => {
            void notify({
                html: '<p>' + getApiErrorMessage(error) + '</p>',
                icon: 'error',
                title: 'Attention !',
                confirmButtonText: "J'ai compris",
            });
        },
    });
